// DEPENDENCIES
import { useEffect } from "react";
import { FormikProps, FormikValues } from "formik";
import { NavigateFunction } from "react-router-dom";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import * as Sentry from "@sentry/react";

// TYPES
import {
  Template,
  PersonType,
  DraftUUID,
  ComponentStep,
  StepTwoPF,
} from "../../types";
import { AppDispatch, store } from "../../app/store";

// UTILS
import { goToStep, handleSendStep, isDev } from "../../app/utils";
import {
  onUpdateCompletedForm,
  onUpdateFormData,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdateOnboardingType,
  onUpdateTheme,
} from "../../reducers/activeTemplateReducer";
import { completeDraft } from "../../connectors/connectors";
import {
  genericToastifyError,
  getDniFromCuit,
  isAllowedRedirectUrl,
} from "../../utils";
import { getCompanyData, getMerchantKey } from "../../utils/companies";
import getTemplateData from "../../hooks/useTemplate";
import { createOnboarding } from "../../steps/physical/basicInformationStep";

// STEPS
import infoStep from "../../steps/physical/infoStep";
import tokenStep from "../../steps/physical/tokenStep";
import identityStep from "../../steps/physical/identityStep";
import personalInformationStep from "../../steps/physical/personalInformationStep";
import employmentProfileStep from "../../steps/physical/employmentProfileStep";

import bodyIcon from "../../assets/images/icons/ui/body.svg";

const devInvestmentChoices = [
  "8f54cac1-6415-41b6-ab47-ca004d387378", // Experiencia: Nada
  "c1e12fc2-68de-462b-9612-08ff55bc3838", // Objetivo: Preservar
  "bf2a03f7-3adb-4e6a-aa7e-0c1e831d6084", // Riesgo: Bajo
  "42e7d801-dc49-4609-8dcf-16693f2b3bb6", // Horizonte de Inversión : 1 año
  "a19dafed-86e1-4214-8110-49275b423286", // Porcentaje Ahorros: 25%
  "45a09168-bc42-45f5-b462-4e92507a205a", // Origen de los Fondos: Ingresos Mensuales
  "9eca9a45-78d1-48b4-ada0-bb35e07609b5", // Ingresos Mensuales: Menos de 2M
  "11a68677-d087-4c2d-89aa-4c8a7c1efd67", // Patrimonio Total: Menos de 18M
];

const prodInvestmentChoices = [
  "fd3c528f-8198-4f02-8831-ec9769703c42", // Experiencia: Nada
  "2984ae74-2a05-4116-8453-16a87c0e4c18", // Objetivo: Preservar
  "86f8b2e9-9b3b-4f38-aa1f-3bd979f6a7b8", // Riesgo: Bajo
  "700f2ebd-7dea-445b-a521-542724ebdff3", // Horizonte de Inversión : 1 año
  "bf0b9a85-cbfc-44f2-8809-710de9845858", // Porcentaje Ahorros: 25%
  "780b9f28-c30b-4c9b-bd56-7fd0de07a899", // Origen de los Fondos: Ingresos Mensuales
  "cf5fd887-7899-49eb-a636-9f8f8922c2cd", // Ingresos Mensuales: Menos de 2M
  "f2174878-d86a-443a-9904-db70249e54ab", // Patrimonio Total: Menos de 18M
];

interface Sav3PreloadStepProps {
  dispatch: AppDispatch;
  formik: FormikProps<FormikValues>;
}

const Sav3PreloadStep = ({ dispatch, formik }: Sav3PreloadStepProps) => {
  const urlParams = new URLSearchParams(window.location.search);
  const cuitFromUrl = urlParams.get("cuit") ?? "";
  const emailFromUrl = urlParams.get("email")?.replace(/ /g, "+") ?? "";
  // `ref` format: "<company> <merchant> <accountId>" — `+` separators in the
  // query string decode to spaces; `filter(Boolean)` tolerates trailing `+`.
  const merchant =
    (urlParams.get("ref") ?? "")
      .replace(/ /g, "+")
      .split("+")
      .filter(Boolean)[1] || "";

  const { setValues } = formik;

  useEffect(() => {
    const init = async () => {
      try {
        const baseValues = {
          cuit: cuitFromUrl,
          email: emailFromUrl,
          identityDocument: getDniFromCuit(cuitFromUrl),
          referrer: "SAV3",
          merchant: merchant,
        };
        await createOnboarding(dispatch, baseValues, true);
        dispatch(onUpdateFormData(baseValues));
        setValues(baseValues);
        goToStep({ dispatch, step: 2 });
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        showToastMessage(genericToastifyError, {
          containerId: "layoutPage",
          type: "ERROR",
        });
      }
    };
    if (cuitFromUrl && emailFromUrl) {
      init();
    }
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <LoadingCircle />
    </div>
  );
};

const stepPreload: ComponentStep = {
  step: 1,
  name: "Información básica",
  hideFromStepper: true,
  disableBackNavigation: true,
  component: (dispatch, _, formik) => {
    if (!dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    return <Sav3PreloadStep dispatch={dispatch} formik={formik} />;
  },
};

const onboardingSav3 = async (
  draftUUID: DraftUUID,
  dispatch: AppDispatch,
  formValues?: FormikValues | null,
  navigate?: NavigateFunction,
): Promise<Template> => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get("email")?.replace(/ /g, "+") ?? "";
    const rawRedirectUrl = urlParams.get("redirect_url") ?? "";
    const merchant = getMerchantKey(formValues);
    const sav3Partnership = getCompanyData().partnerships?.find(
      (p) => p.key === "sav3",
    );
    const merchantConfig = sav3Partnership?.merchants?.find(
      (m) => m.key.toLowerCase() === merchant.toLowerCase(),
    );
    const merchantTheme =
      merchantConfig?.theme ?? sav3Partnership?.theme ?? "SAV3";
    const sav3AllowedDomains =
      sav3Partnership?.successRedirect?.allowedDomains ?? [];
    const redirectUrlFromUrl =
      rawRedirectUrl && isAllowedRedirectUrl(rawRedirectUrl, sav3AllowedDomains)
        ? rawRedirectUrl
        : "";

    const {
      favicon,
      formTitle,
      logo,
      countries,
      countriesOptions,
      citizenshipOptions,
      activitiesOptions,
      getProvincesFilteredByCountry,
      provinces,
    } = await getTemplateData({
      draftUUID,
      formValues,
    });

    // employmentProfileStep: acts as the "preconfirmation" — sends all steps in sequence
    const employmentOnSendStep = async (
      values: FormikValues | null,
      step: Record<string, StepTwoPF>,
    ) => {
      const stepTwo = step.stepTwo;
      const email = values?.email ?? emailFromUrl;

      await handleSendStep({
        dispatch,
        personType: "PHYSICAL" as PersonType,
        email,
        sendedSteps: store.getState().onboardingData.sendedSteps,
        stepId: 2,
        step: stepTwo,
      });

      await handleSendStep({
        dispatch,
        personType: "PHYSICAL" as PersonType,
        email,
        sendedSteps: store.getState().onboardingData.sendedSteps,
        stepId: 3,
        step: { "@type": "3_PHYSICAL", coOwners: [] },
      });

      await handleSendStep({
        dispatch,
        personType: "PHYSICAL" as PersonType,
        email,
        sendedSteps: store.getState().onboardingData.sendedSteps,
        stepId: 4,
        step: {
          "@type": "4_PHYSICAL",
          qualifiedInvestorType: "NONE",
          investmentProfile: {
            choices: isDev ? devInvestmentChoices : prodInvestmentChoices,
          },
        },
      });

      await handleSendStep({
        dispatch,
        personType: "PHYSICAL" as PersonType,
        email,
        sendedSteps: store.getState().onboardingData.sendedSteps,
        stepId: 5,
        step: {
          "@type": "5_PHYSICAL",
          acceptedTermsAndConditions: true,
          acceptedOptionalTermsAndConditions: false,
        },
      });

      await handleSendStep({
        dispatch,
        personType: "PHYSICAL" as PersonType,
        email,
        sendedSteps: store.getState().onboardingData.sendedSteps,
        stepId: 6,
        step: {
          "@type": "6_PHYSICAL",
          coOwnerSteps: [],
        },
      });

      await completeDraft({
        draftUUID: store.getState().onboardingData.draftUUID,
      });
      dispatch(onUpdateCompletedForm(true));
      dispatch({ type: "reset-store" });
      dispatch(onUpdateOnboardingType("PHYSICAL"));
      // reset-store wipes activeTemplate.theme; re-dispatch so SuccessPage's
      // Layout reads the merchant/partnership theme from Redux normally.
      dispatch(onUpdateTheme(merchantTheme));
      const successParams = new URLSearchParams({ sav3: "true" });
      if (redirectUrlFromUrl) {
        const accountId =
          (urlParams.get("ref") ?? "")
            .replace(/ /g, "+")
            .split("+")
            .filter(Boolean)[2] || "";
        const redirectUrl = new URL(redirectUrlFromUrl);
        if (accountId) {
          redirectUrl.searchParams.set("accountId", accountId);
        }
        successParams.set("redirect_url", redirectUrl.toString());
      }
      if (merchantConfig) {
        successParams.set("merchant", merchantConfig.key);
      }
      navigate?.(`/registro-enviado?${successParams.toString()}`);
    };

    const sav3Steps = [
      tokenStep({ step: 2 }),
      identityStep(countries, { hideEditDni: true }),
      personalInformationStep(
        countriesOptions,
        citizenshipOptions,
        getProvincesFilteredByCountry,
        provinces,
      ),
      employmentProfileStep(
        activitiesOptions,
        countriesOptions,
        getProvincesFilteredByCountry,
        provinces,
        employmentOnSendStep,
      ),
    ];

    return {
      onboardingType: "PHYSICAL",
      formTitle,
      logo,
      icon: bodyIcon,
      favicon,
      steps: [
        stepPreload,
        ...sav3Steps.map((step, index) => ({
          step: index + 2,
          displayStep: index + 1,
          ...step,
        })),
        infoStep({ oldAuth: true }),
      ],
      theme: merchantTheme,
      preconfirmation: null,
      remoteSteps: [
        {
          link: "/dni",
          step: identityStep(countries).subSteps?.find(
            ({ label }) => label === "uploadDNI",
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: identityStep(countries).subSteps?.find(
            ({ label }) => label === "livenessValidation",
          ) as ComponentStep,
        },
      ],
    };
  } catch (error) {
    throw new Error(error as string);
  }
};

export default onboardingSav3;
